import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchHabits, deleteHabit, updateHabit, updateHabitOrderLocal, reorderHabitsInDB } from '../store/slices/habitsSlice';
import { fetchWeeklyView } from '../store/slices/recordsSlice';
import EditHabitModal from './EditHabitModal';
import { useTranslation } from '../hooks/useTranslation';
import { Habit } from '../store/slices/habitsSlice';
import '../styles/HabitManagement.css';

interface HabitManagementProps {
  onClose: () => void;
}

const HabitManagement: React.FC<HabitManagementProps> = ({ onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { habits } = useSelector((state: RootState) => state.habits);
  const [editingHabitId, setEditingHabitId] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [localHabits, setLocalHabits] = useState<Habit[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const { t } = useTranslation();

  // Initial load - fetch all habits including archived
  useEffect(() => {
    dispatch(fetchHabits(true)); // Always fetch all habits including archived
  }, [dispatch]);

  useEffect(() => {
    // Filter habits based on archive status
    if (Array.isArray(habits)) {
      const filtered = habits.filter(h => showArchived ? h.isArchived === 1 : h.isArchived === 0);
      setLocalHabits(filtered);
    } else {
      setLocalHabits([]);
    }
  }, [habits, showArchived]);

  const handleDelete = async (habitId: number, habitName: string) => {
    if (window.confirm(t('habit.deleteConfirm').replace('{name}', habitName))) {
      await dispatch(deleteHabit(habitId)).unwrap();
      dispatch(fetchWeeklyView());
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newHabits = [...localHabits];
    const draggedHabit = newHabits[draggedIndex];
    
    // Remove element and insert at new position
    newHabits.splice(draggedIndex, 1);
    newHabits.splice(dropIndex, 0, draggedHabit);
    
    // UIを即時更新
    setLocalHabits(newHabits);
    dispatch(updateHabitOrderLocal(newHabits));
    setDraggedIndex(null);

    // IndexedDB の並び順を更新
    await dispatch(reorderHabitsInDB(newHabits)).unwrap();
    dispatch(fetchWeeklyView());
  };

  const handleArchive = async (habitId: number, habitName: string) => {
    if (window.confirm(t('habit.archiveConfirm').replace('{name}', habitName))) {
      await dispatch(updateHabit({ 
        id: habitId, 
        data: { isArchived: 1 } 
      })).unwrap();
      // Refetch all habits including archived
      dispatch(fetchHabits(true));
      dispatch(fetchWeeklyView());
    }
  };

  const handleRestore = async (habitId: number) => {
    await dispatch(updateHabit({ 
      id: habitId, 
      data: { isArchived: 0 } 
    })).unwrap();
    // Refetch all habits including archived
    dispatch(fetchHabits(true));
    dispatch(fetchWeeklyView());
    // Switch to active habits view after restore
    setShowArchived(false);
  };

  return (
    <div className="habit-management-overlay" onClick={onClose}>
      <div className="habit-management-content" onClick={(e) => e.stopPropagation()}>
        <div className="habit-management-header">
          <h2>📝 {t('habit.management')}</h2>
          <button className="habit-management-close" onClick={onClose}>×</button>
        </div>
        
        <div className="archive-toggle">
          <button 
            className={`archive-toggle-btn ${!showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(false)}
          >
            📋 {t('habit.activeHabits')}
          </button>
          <button 
            className={`archive-toggle-btn ${showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(true)}
          >
            📦 {t('habit.archivedHabits')}
          </button>
        </div>
        
        <div className="habit-management-body">
          {localHabits.length === 0 ? (
            <div className="no-habits">
              <p>{t('habit.noHabits')}</p>
              <p>{t('habit.addPrompt')}</p>
            </div>
          ) : (
            <>
              <div className="management-instructions">
                <p>💡 {t('habit.managementInstructions')}</p>
              </div>
              
              <div className="habit-list-management">
                {localHabits.map((habit, index) => (
                  <div
                    key={habit.id}
                    className={`habit-item-management ${draggedIndex === index ? 'dragging' : ''}`}
                    draggable={!showArchived}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    {!showArchived && (
                      <div className="habit-drag-handle" title={t('habit.dragToReorder')}>
                        ☰
                      </div>
                    )}
                    
                    <div className="habit-info">
                      <span className="habit-icon">{habit.icon || '📝'}</span>
                      <span className="habit-name">{habit.name}</span>
                      <div className="habit-details">
                        <div className="habit-weekdays">
                          {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((dayKey) => {
                            const isActive = habit.repeatDays && habit.repeatDays[dayKey as keyof typeof habit.repeatDays];
                            return (
                              <span 
                                key={dayKey} 
                                className={`weekday-indicator ${isActive ? 'active' : 'inactive'}`}
                              >
                                {t(`weekday.${dayKey}`)}
                              </span>
                            );
                          })}
                        </div>
                        <span className="habit-detail">
                          {habit.goalType === 'continuous' ? t('habit.continuous') : `${habit.weeklyGoal}${t('habit.perWeek')}`}
                        </span>
                        <div 
                          className="habit-color-preview" 
                          style={{ backgroundColor: habit.color }}
                        />
                      </div>
                    </div>
                    
                    <div className="habit-actions">
                      <button
                        className="action-btn edit"
                        onClick={() => setEditingHabitId(habit.id!)}
                        title={t('common.edit')}
                      >
                        ✏️
                      </button>
                      
                      {!showArchived ? (
                        <button
                          className="action-btn archive"
                          onClick={() => handleArchive(habit.id!, habit.name)}
                          title={t('habit.archive')}
                        >
                          📦
                        </button>
                      ) : (
                        <button
                          className="action-btn restore"
                          onClick={() => handleRestore(habit.id!)}
                          title={t('habit.restore')}
                        >
                          ↩️
                        </button>
                      )}
                      
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(habit.id!, habit.name)}
                        title={t('common.delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      
      {editingHabitId && (
        <EditHabitModal
          isOpen={!!editingHabitId}
          habit={habits.find(h => h.id === editingHabitId)}
          onClose={() => setEditingHabitId(null)}
        />
      )}
    </div>
  );
};

export default HabitManagement;
